import { createSlice } from '@reduxjs/toolkit';

import {
  COMMENTS_STATE_CREATE,
  COMMENTS_STATE_DELETE,
  COMMENTS_STATE_EDIT,
  COMMENTS_STATE_INITIAL,
  COMMENTS_STATE_REPLY,
  STATUS_DELETED,
  STATUS_PARENT_DELETED,
  STATUS_WAIT_SERVER_APPROVE,
} from '../../constants/comments';

import { getFormattedComment } from '../../../common/helpers/comments';
import { getFormattedUser } from '../../../common/helpers/user';

const initialState = {
  isLoading: true,
  userList: [],
  summaryEntries: [],
  activeMessageCount: 0,
  deletedChildCount: 0,
  deletedMessageCount: 0,
  comments: [],
  totalCount: 0,
  selectedId: null,
  selectedTime: null,
  commentsStateType: COMMENTS_STATE_INITIAL,
  commentsStateCommentId: null,
};

/** https://immerjs.github.io/immer/update-patterns/ */
export const slice = createSlice({
  name: 'comments',
  initialState,
  reducers: {
    setLoadingStateAction: (state, action) => {
      state.isLoading = action.payload;
    },
    getUserListAction: (state) => {
      state.isLoading = true;
    },
    setUserListAction: (state, action) => {
      state.userList = action.payload.map(getFormattedUser);
      state.isLoading = false;
    },
    getCommentsAndSummaryAction: (state) => state,
    createCommentAction: (state, action) => {
      state.totalCount += 1;
      state.activeMessageCount += 1;

      const getInsertIndex = () => {
        if (!action.payload.parentId) {
          const tcIndex = state.comments.findIndex((comment$) => comment$.time > action.payload.time);

          return tcIndex === -1 ? state.comments.length : tcIndex;
        }

        const parentIndex = state.comments.findIndex(({ id }) => id === action.payload.parentId);
        const parent = state.comments[parentIndex];

        parent.activeChildCount += 1;

        const tcIndex = state.comments.findIndex(
          (comment$, index$) => index$ > parentIndex && comment$.parentId !== action.payload.parentId,
        );

        return tcIndex === -1 ? state.comments.length : tcIndex;
      };

      state.comments.splice(
        getInsertIndex(),
        0,
        getFormattedComment({
          id: action.payload.targetReplaceId,
          parentId: action.payload.parentId,
          author: action.payload.author,
          time: action.payload.time,
          html: action.payload.html,
          status: STATUS_WAIT_SERVER_APPROVE,
        }),
      );

      const summaryPoint = state.summaryEntries.find(({ time }) => action.payload.time === time);

      if (summaryPoint) {
        summaryPoint.activeChildCount += 1;
      } else {
        const tcsIndex = state.summaryEntries.findIndex((comment$) => comment$.time > action.payload.time);

        state.summaryEntries.splice(tcsIndex, 0, {
          time: action.payload.time,
          activeChildCount: 1,
        });
      }
    },
    createCommentFinishedAction: (state, action) => {
      state.comments.forEach((comment, index) => {
        if (comment.id === action.payload.targetReplaceId) {
          state.comments[index] = getFormattedComment(action.payload.comment);
        }
      });
    },
    editCommentAction: (state, action) => {
      state.comments.forEach((comment) => {
        if (comment.id === action.payload.commentId) {
          // eslint-disable-next-line no-param-reassign
          comment.html = action.payload.html;
          // eslint-disable-next-line no-param-reassign
          comment.updateCount += 1;
        }
      });
    },
    deleteCommentAction: (state, action) => {
      const targetComment = state.comments.find((comment) => comment.id === action.payload.id);
      const summaryPoint = state.summaryEntries.find(({ time }) => targetComment.time === time);

      targetComment.status = STATUS_DELETED;

      state.totalCount -= 1;
      state.deletedMessageCount += 1;
      state.activeMessageCount -= 1;

      summaryPoint.activeChildCount -= 1;

      if (targetComment.parentId) {
        const parent = state.comments.find(({ id }) => id === targetComment.parentId);

        parent.activeChildCount -= 1;
        parent.deletedChildCount += 1;
      } else {
        state.comments.forEach((comment$) => {
          if (
            ![STATUS_DELETED, STATUS_PARENT_DELETED].includes(comment$.status) &&
            comment$.parentId === action.payload.id
          ) {
            // eslint-disable-next-line no-param-reassign
            comment$.status = STATUS_PARENT_DELETED;

            targetComment.activeChildCount -= 1;
            targetComment.deletedChildCount += 1;

            state.totalCount -= 1;
            state.deletedMessageCount += 1;
            state.activeMessageCount -= 1;

            summaryPoint.activeChildCount -= 1;
          }
        });
      }
    },
    setCommentsAndSummaryAction: (state, action) => {
      const { comments, summary } = action.payload;

      state.comments = comments.map(getFormattedComment);
      state.totalCount = state.comments.filter(
        (comment) => ![STATUS_DELETED, STATUS_PARENT_DELETED].includes(comment.status),
      ).length;

      state.summaryEntries = summary.entries;
      state.activeMessageCount = summary.activeMessageCount;
      state.deletedMessageCount = summary.deletedMessageCount;

      state.isLoading = false;
    },
    clearCommentsAction: (state) => {
      state.comments = [];
      state.totalCount = 0;
      state.summaryEntries = [];
      state.activeMessageCount = 0;
      state.deletedMessageCount = 0;
      state.isLoading = true;
    },
    setSelectedCommentIdAction: (state, action) => {
      state.selectedId = action.payload;
      state.selectedTime = null;
    },
    setSelectedCommentTimeAction: (state, action) => {
      state.selectedId = null;
      state.selectedTime = action.payload;
    },
    setCommentsInitialStateAction: (state) => {
      state.commentsStateType = COMMENTS_STATE_INITIAL;
      state.commentsStateCommentId = null;
    },
    setCommentsCreateStateAction: (state) => {
      state.commentsStateType = COMMENTS_STATE_CREATE;
      state.commentsStateCommentId = null;
    },
    setCommentsEditStateAction: (state, action) => {
      state.commentsStateType = COMMENTS_STATE_EDIT;
      state.commentsStateCommentId = action.payload;
    },
    setCommentsDeleteStateAction: (state, action) => {
      state.commentsStateType = COMMENTS_STATE_DELETE;
      state.commentsStateCommentId = action.payload;
    },
    setCommentsReplyStateAction: (state, action) => {
      state.commentsStateType = COMMENTS_STATE_REPLY;
      state.commentsStateCommentId = action.payload;
    },
  },
});

export const {
  getCommentsAndSummaryAction,
  createCommentAction,
  createCommentFinishedAction,
  editCommentAction,
  deleteCommentAction,
  setCommentsAndSummaryAction,
  clearCommentsAction,
  getUserListAction,
  setUserListAction,
  setSelectedCommentIdAction,
  setSelectedCommentTimeAction,
  setCommentsInitialStateAction,
  setCommentsCreateStateAction,
  setCommentsEditStateAction,
  setCommentsDeleteStateAction,
  setCommentsReplyStateAction,
} = slice.actions;
